// Vendor registry — target vendors per must-win category, with onboarding
// status. Write-through to the worker (KV prefix `v:`) so the ManCo shares
// one truth on who's on-line vs in pipeline vs blocked.
//
// !!! PLACEHOLDER target lists. Kabelo to replace with the real targets.
// The `id` strings appear in action-impact paths
// (e.g. "s1:fuel:shell"), so keep them stable once shipped.

import { NOTES_ENDPOINT } from "../config/endpoints";

export type VendorStatus =
  | "live" // partner is on, transacting
  | "signed" // contract signed, integration in flight
  | "pipeline" // in commercial conversation
  | "blocked" // commercial conversation stalled
  | "off-list"; // we've decided not to pursue

export interface Vendor {
  id: string;
  /** Category code (see categories.ts) — duplicated for fast lookup. */
  categoryCode: string;
  /** Which side this vendor serves — most vendors serve both (issue + redeem). */
  sides: ("supply" | "demand")[];
  /** Display name. */
  name: string;
  /** Current onboarding status. */
  status: VendorStatus;
  /** Person leading this vendor's commercial. "" if unassigned. */
  owner: string;
  /** Free-text notes about the vendor's current state. */
  notes: string;
  /** ISO date of last meaningful update. */
  updatedAt: string;
  /** Who last edited the row. */
  updatedBy: string;
  synced?: boolean;
}

const STORE_KEY = "bvndle-cc:vendors";

function loadLocal(): Vendor[] {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || "[]") as Vendor[];
  } catch {
    return [];
  }
}
function saveLocal(rows: Vendor[]): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(rows));
  } catch {
    /* private mode */
  }
}
function nowISO(): string {
  return new Date().toISOString();
}

// ---------------- seed data ----------------------------------------------

/**
 * Seed targets per category. Used on first boot to populate the registry so
 * the drill-down isn't empty. Replaced/edited in the running app — seeds are
 * not re-applied after first boot.
 *
 * !!! PLACEHOLDERS — replace with the real target list.
 */
const SEED_VENDORS: Omit<Vendor, "updatedAt" | "updatedBy" | "synced">[] = [
  // Fuel
  { id: "shell", categoryCode: "fuel", sides: ["supply", "demand"], name: "Shell", status: "pipeline", owner: "", notes: "" },
  { id: "total", categoryCode: "fuel", sides: ["supply", "demand"], name: "TotalEnergies", status: "pipeline", owner: "", notes: "" },
  { id: "mobil", categoryCode: "fuel", sides: ["supply", "demand"], name: "Mobil", status: "pipeline", owner: "", notes: "" },
  { id: "nnpc", categoryCode: "fuel", sides: ["supply", "demand"], name: "NNPC Retail", status: "pipeline", owner: "", notes: "" },
  { id: "conoil", categoryCode: "fuel", sides: ["supply", "demand"], name: "Conoil", status: "pipeline", owner: "", notes: "" },

  // Groceries
  { id: "shoprite", categoryCode: "groceries", sides: ["supply", "demand"], name: "Shoprite", status: "pipeline", owner: "", notes: "" },
  { id: "spar", categoryCode: "groceries", sides: ["supply", "demand"], name: "SPAR", status: "pipeline", owner: "", notes: "" },
  { id: "ebeano", categoryCode: "groceries", sides: ["supply", "demand"], name: "Ebeano", status: "pipeline", owner: "", notes: "" },
  { id: "hubmart", categoryCode: "groceries", sides: ["supply", "demand"], name: "Hubmart", status: "pipeline", owner: "", notes: "" },

  // Mobile data
  { id: "mtn-data", categoryCode: "telecoms-data", sides: ["supply", "demand"], name: "MTN", status: "live", owner: "", notes: "" },
  { id: "glo-data", categoryCode: "telecoms-data", sides: ["supply", "demand"], name: "Glo", status: "live", owner: "", notes: "" },
  { id: "airtel-data", categoryCode: "telecoms-data", sides: ["supply", "demand"], name: "Airtel", status: "live", owner: "", notes: "" },
  { id: "9mobile-data", categoryCode: "telecoms-data", sides: ["supply", "demand"], name: "9mobile", status: "live", owner: "", notes: "" },

  // Airtime
  { id: "mtn-air", categoryCode: "telecoms-airtime", sides: ["supply", "demand"], name: "MTN", status: "live", owner: "", notes: "" },
  { id: "glo-air", categoryCode: "telecoms-airtime", sides: ["supply", "demand"], name: "Glo", status: "live", owner: "", notes: "" },
  { id: "airtel-air", categoryCode: "telecoms-airtime", sides: ["supply", "demand"], name: "Airtel", status: "live", owner: "", notes: "" },
  { id: "9mobile-air", categoryCode: "telecoms-airtime", sides: ["supply", "demand"], name: "9mobile", status: "live", owner: "", notes: "" },

  // Bill payments
  { id: "ikeja-elec", categoryCode: "bill-payments", sides: ["demand"], name: "Ikeja Electric", status: "pipeline", owner: "", notes: "" },
  { id: "eko-elec", categoryCode: "bill-payments", sides: ["demand"], name: "Eko Electric", status: "pipeline", owner: "", notes: "" },
  { id: "dstv", categoryCode: "bill-payments", sides: ["demand"], name: "DStv", status: "pipeline", owner: "", notes: "" },
  { id: "gotv", categoryCode: "bill-payments", sides: ["demand"], name: "GOtv", status: "pipeline", owner: "", notes: "" },

  // Card transactions
  { id: "verve", categoryCode: "card-transactions", sides: ["demand"], name: "Verve", status: "pipeline", owner: "", notes: "" },
  { id: "mastercard", categoryCode: "card-transactions", sides: ["demand"], name: "Mastercard", status: "pipeline", owner: "", notes: "" },
  { id: "visa", categoryCode: "card-transactions", sides: ["demand"], name: "Visa", status: "pipeline", owner: "", notes: "" },

  // Naira transfers
  { id: "vfd-rail", categoryCode: "naira-transfers", sides: ["supply", "demand"], name: "VFD Rail", status: "signed", owner: "Kabelo", notes: "" },
  { id: "nibss", categoryCode: "naira-transfers", sides: ["supply", "demand"], name: "NIBSS", status: "pipeline", owner: "", notes: "" },

  // Gift cards
  { id: "amazon-gc", categoryCode: "gift-cards", sides: ["demand"], name: "Amazon", status: "pipeline", owner: "", notes: "" },
  { id: "shoprite-gc", categoryCode: "gift-cards", sides: ["demand"], name: "Shoprite GC", status: "pipeline", owner: "", notes: "" },
  { id: "apple-gc", categoryCode: "gift-cards", sides: ["demand"], name: "Apple", status: "pipeline", owner: "", notes: "" },

  // Pharmacies
  { id: "medplus", categoryCode: "pharmacies", sides: ["supply", "demand"], name: "MedPlus", status: "pipeline", owner: "", notes: "" },
  { id: "healthplus", categoryCode: "pharmacies", sides: ["supply", "demand"], name: "HealthPlus", status: "pipeline", owner: "", notes: "" },
  { id: "alpha-pharma", categoryCode: "pharmacies", sides: ["supply", "demand"], name: "Alpha Pharmacy", status: "pipeline", owner: "", notes: "" },
];

const SEED_FLAG_KEY = "bvndle-cc:vendors:seeded";

function maybeSeed(): void {
  if (localStorage.getItem(SEED_FLAG_KEY)) return;
  const existing = loadLocal();
  if (existing.length > 0) {
    localStorage.setItem(SEED_FLAG_KEY, "1");
    return;
  }
  const now = nowISO();
  const seeded: Vendor[] = SEED_VENDORS.map((v) => ({
    ...v,
    updatedAt: now,
    updatedBy: "seed",
    synced: false,
  }));
  saveLocal(seeded);
  localStorage.setItem(SEED_FLAG_KEY, "1");
}

// ---------------- API ----------------------------------------------------

export async function listVendors(): Promise<Vendor[]> {
  maybeSeed();
  const local = loadLocal();
  let remote: Vendor[] = [];
  if (NOTES_ENDPOINT) {
    try {
      const r = await fetch(`${NOTES_ENDPOINT.replace(/\/$/, "")}/vendors`);
      if (r.ok) {
        const data = (await r.json()) as { vendors?: Vendor[] };
        remote = (data.vendors ?? []).map((v) => ({ ...v, synced: true }));
      }
    } catch {
      /* fall back to local */
    }
  }
  const byId = new Map<string, Vendor>();
  for (const v of local) byId.set(v.id, v);
  for (const v of remote) byId.set(v.id, v); // remote wins
  return Array.from(byId.values()).sort((a, b) =>
    a.categoryCode === b.categoryCode
      ? a.name.localeCompare(b.name)
      : a.categoryCode.localeCompare(b.categoryCode),
  );
}

export function vendorsForCategory(vendors: Vendor[], categoryCode: string, side: "supply" | "demand"): Vendor[] {
  return vendors.filter((v) => v.categoryCode === categoryCode && v.sides.includes(side));
}

export interface UpsertVendorInput {
  id?: string;
  categoryCode: string;
  sides: ("supply" | "demand")[];
  name: string;
  status: VendorStatus;
  owner: string;
  notes: string;
  updatedBy: string;
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 32);
}

export async function upsertVendor(input: UpsertVendorInput): Promise<Vendor> {
  const id = input.id ?? `${slugify(input.name)}-${Math.random().toString(36).slice(2, 6)}`;
  const row: Vendor = {
    id,
    categoryCode: input.categoryCode,
    sides: input.sides.length > 0 ? input.sides : ["supply", "demand"],
    name: input.name.trim(),
    status: input.status,
    owner: input.owner.trim(),
    notes: input.notes.trim(),
    updatedAt: nowISO(),
    updatedBy: input.updatedBy,
    synced: false,
  };
  const local = loadLocal();
  const idx = local.findIndex((v) => v.id === id);
  if (idx >= 0) local[idx] = row;
  else local.push(row);
  saveLocal(local);

  if (NOTES_ENDPOINT) {
    try {
      const r = await fetch(`${NOTES_ENDPOINT.replace(/\/$/, "")}/vendors`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(row),
      });
      if (r.ok) {
        row.synced = true;
        const after = loadLocal().map((v) => (v.id === row.id ? row : v));
        saveLocal(after);
      }
    } catch {
      /* stays local */
    }
  }
  return row;
}

export async function deleteVendor(id: string): Promise<void> {
  const local = loadLocal().filter((v) => v.id !== id);
  saveLocal(local);
  if (NOTES_ENDPOINT) {
    try {
      await fetch(`${NOTES_ENDPOINT.replace(/\/$/, "")}/vendors/${id}`, { method: "DELETE" });
    } catch {
      /* localStorage already updated */
    }
  }
}

export const STATUS_LABEL: Record<VendorStatus, string> = {
  live: "LIVE",
  signed: "SIGNED",
  pipeline: "PIPELINE",
  blocked: "BLOCKED",
  "off-list": "OFF-LIST",
};

export const STATUS_COLOR: Record<VendorStatus, string> = {
  live: "#5ec98c",
  signed: "var(--purple)",
  pipeline: "var(--ink-dim)",
  blocked: "var(--coral)",
  "off-list": "var(--ink-faint)",
};
